import {
  Box,
  Divider,
  TableBody,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  PLAN_NAME_ENUM,
  type PlanInfo,
  type PlanInfoTableRow,
} from '@query/page-query/pricing-query/pricing.model'
import { Icon } from '@media-components'
import {
  HeaderCell,
  IconButtonStyled,
  InfoTableCell,
  InfoTableStyled,
  TableCelWrapper,
  ValueWrapper,
} from './styles.component'

const defaultVlaues = {
  available: false,
  plan_name: PLAN_NAME_ENUM.GROWTH,
  value: '0',
}

function RenderValue({ value }: { value: PlanInfo }) {
  if (value.available && value.value === '0') {
    return (
      <Box
        sx={{
          textAlign: 'center',
          display: 'flex',
          justifyContent: {
            xs: 'center',
            sm: 'space-around',
          },
          alignItems: 'center',
          marginLeft: 'auto',
          marginRight: 'auto',
          minWidth: {
            xs: '100%',
            sm: 'unset',
          },
        }}
      >
        <Icon
          iconName="TickCloseCircleSolid"
          type="solid"
          currentValue={false}
        />
      </Box>
    )
  }

  return value.value ? (
    <Typography
      variant="body2.500"
      sx={{
        display: 'flex',
        justifyContent: {
          xs: 'center',
          sm: 'space-around',
        },
        alignItems: 'start',
        width: '100%',
        fontSize: {
          xs: '0.75rem',
          sm: '0.875rem',
        },
      }}
      minWidth={{
        xs: '100%',
        sm: 'auto',
      }}
      textAlign={{
        xs: 'right',
        sm: 'center',
      }}
    >
      {value.value === '-1' ? 'Unlimited' : value.value}
    </Typography>
  ) : null
}

interface PlanInfoTableProps {
  title?: string
  data: PlanInfoTableRow[]
  index: number
}

export function InfoTable({ title, data, index }: PlanInfoTableProps) {
  const formattedTitle = title?.split('_').join(' ')
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '100%',
      }}
    >
      {index !== 0 && <Divider sx={{ mb: 4, mt: 4 }} />}
      {formattedTitle && index !== 0 && (
        <Typography
          variant="body1.700"
          sx={{
            marginBottom: '1rem',
          }}
        >
          {formattedTitle}
        </Typography>
      )}
      <InfoTableStyled>
        {index === 0 && (
          <TableHead>
            <TableRow>
              <InfoTableCell colSpan={5}>
                <TableCelWrapper index={-1}>
                  <HeaderCell>
                    {formattedTitle && (
                      <Typography variant="body1.700">
                        {formattedTitle}
                      </Typography>
                    )}
                  </HeaderCell>
                </TableCelWrapper>
              </InfoTableCell>
            </TableRow>
          </TableHead>
        )}
        <TableBody sx={{ mt: 2 }}>
          {data.map((row, index) => (
            <TableRow key={row.feature_name}>
              <InfoTableCell colSpan={5}>
                <TableCelWrapper
                  index={index}
                  sx={{
                    gap: {
                      xs: 'unset',
                      sm: '70px',
                    },
                  }}
                >
                  <HeaderCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: {
                          xs: '0.75rem',
                          sm: '0.875rem',
                        },
                        flex: 1,
                      }}
                    >
                      {row.feature_name}
                    </Typography>
                    {row.feature_tool_tip && (
                      <Tooltip
                        title={row.feature_tool_tip}
                        placement="top"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              minHeight: 'fit-content !important',
                            },
                          },
                        }}
                      >
                        <IconButtonStyled disableRipple>
                          <Icon
                            iconName="InfoCircle"
                            fontSize="small"
                            type="solid"
                          />
                        </IconButtonStyled>
                      </Tooltip>
                    )}
                  </HeaderCell>

                  <Box sx={{ display: 'flex', gap: 2, marginLeft: 'auto' }}>
                    <ValueWrapper
                      sx={{
                        display: 'flex',
                      }}
                    >
                      <RenderValue
                        value={
                          row.plan_info.find(
                            (i: PlanInfo) =>
                              i.plan_name === PLAN_NAME_ENUM.GROWTH
                          ) || defaultVlaues
                        }
                      />
                    </ValueWrapper>
                    <ValueWrapper
                      sx={{
                        display: 'flex',
                        backgroundColor: 'transparent',
                        borderLeft: {
                          xs: 'none',
                          sm: 'none',
                        },
                        borderRight: {
                          xs: 'none',
                          sm: 'none',
                        },
                        position: 'relative',
                      }}
                    >
                      <RenderValue
                        value={
                          row.plan_info.find(
                            (i: PlanInfo) =>
                              i.plan_name === PLAN_NAME_ENUM.PREMIUM
                          ) || {
                            ...defaultVlaues,
                            plan_name: PLAN_NAME_ENUM.PREMIUM,
                          }
                        }
                      />
                    </ValueWrapper>
                    <ValueWrapper
                      sx={{
                        display: 'flex',
                      }}
                    >
                      <RenderValue
                        value={
                          row.plan_info.find(
                            (i: PlanInfo) =>
                              i.plan_name === PLAN_NAME_ENUM.ENTERPRISE
                          ) || {
                            ...defaultVlaues,
                            plan_name: PLAN_NAME_ENUM.ENTERPRISE,
                          }
                        }
                      />
                    </ValueWrapper>
                  </Box>
                </TableCelWrapper>
              </InfoTableCell>
            </TableRow>
          ))}
        </TableBody>
      </InfoTableStyled>
    </Box>
  )
}
